import { useQuery } from '@tanstack/react-query'
import { Button, Icon, Loader } from '@susu/ui/components'
import { useI18N } from '@susu/i18n/client'
import { useEffect, useRef } from 'react'

import Avo, { ErrorType } from '../../../Avo/Avo'
import { ScreenLayout } from '../../components/ScreenLayout'
import { NO_PREFERENCE_IMAGE } from '../../config/constants'
import { flowProperties } from '../../lib/analytics'
import { useStyleAdvisorScreen } from '../../lib/feature-flags'
import { useBookingStore } from '../../store'
import { getEmployees } from './api/employees-api'
import {
  getAvailabilityInfo,
  getEmployeeImage,
  getEppUrl,
  sortByEarliestAvailability,
} from './EmployeesList.utils'
import type { Employee } from './EmployeesList.types'

interface EmployeesListProps {
  onClose?: () => void
}

interface EmployeeRowProps {
  employee: Employee
  onSelect: (employee: Employee) => void
  eppUrl: string | null
}

const EmployeeRow = ({ employee, onSelect, eppUrl }: EmployeeRowProps) => {
  const { t } = useI18N()
  const availabilityInfo = getAvailabilityInfo(employee.firstAvailableDate)
  const isAvailableToday = availabilityInfo.status === 'today'
  const isUnavailable = !employee.firstAvailableDate

  // No-preference employees always use the static CDN image; their API
  // fields (name, imageUrl, imageAlternativeUrl) are intentionally null.
  const avatarSrc = employee.isNoPreferenceEmployee
    ? NO_PREFERENCE_IMAGE
    : (getEmployeeImage(employee.imageUrl, employee.imageAlternativeUrl) ??
      NO_PREFERENCE_IMAGE)

  const displayName = employee.isNoPreferenceEmployee
    ? t('employeesList.noPreference')
    : (employee.name ?? '')

  const availabilityLabel = availabilityInfo.date
    ? t(availabilityInfo.key, { date: availabilityInfo.date })
    : t(availabilityInfo.key)

  const avatar = (
    <img
      src={avatarSrc}
      alt={displayName}
      className="w-[76px] h-[76px] rounded-full object-cover shrink-0"
      data-testid={
        employee.isNoPreferenceEmployee
          ? 'employee-no-preference-avatar'
          : 'employee-avatar'
      }
    />
  )

  return (
    <button
      type="button"
      onClick={() => onSelect(employee)}
      className={`border-t border-border flex gap-60 items-center pr-16 py-32 w-full text-left transition-[background-color] ${isUnavailable ? 'cursor-default' : 'hover:bg-surface-alt'}`}
      data-testid={
        employee.isNoPreferenceEmployee
          ? 'employees-list-row-no-preference'
          : 'employees-list-row'
      }
    >
      <div className="flex flex-1 gap-20 items-center min-w-0">
        {eppUrl ? (
          <a
            href={eppUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0"
            data-testid="employee-avatar-link"
          >
            {avatar}
          </a>
        ) : (
          avatar
        )}
        <div className="flex flex-col gap-4 flex-1 min-w-0">
          <p
            className={`text-[18px] leading-[28px] font-medium tracking-[-0.18px] truncate ${isUnavailable ? 'text-text-secondary' : 'text-text'}`}
            data-testid="employee-name"
          >
            {displayName}
          </p>
          <p
            className={`text-base leading-[24px] font-light ${isUnavailable ? 'text-text-secondary' : isAvailableToday ? 'text-text-success' : 'text-text'}`}
            data-testid="employee-availability"
          >
            {availabilityLabel}
          </p>
        </div>
      </div>
      {!isUnavailable && (
        <Icon name="chevron-right" size={16} className="shrink-0 text-icon" />
      )}
    </button>
  )
}

/** Separates the no-preference entry (recognised by isNoPreferenceEmployee) from the rest. */
function splitEmployees(employees: Employee[]): {
  noPreference: Employee | null
  rest: Employee[]
} {
  const noPreference = employees.find((e) => e.isNoPreferenceEmployee) ?? null
  const rest = employees.filter((e) => !e.isNoPreferenceEmployee)
  return { noPreference, rest }
}

/**
 * Returns the no-preference Employee to render as the first row.
 * Uses the API entry directly when present (it carries a real firstAvailableDate).
 * Falls back to a minimal synthetic entry if the API omits it.
 */
function resolveNoPreferenceEmployee(
  apiNoPreference: Employee | null,
): Employee {
  if (apiNoPreference) return apiNoPreference

  // Fallback: synthesise an entry so the row is always shown
  const today = new Date().toISOString().split('T')[0] as string
  return {
    id: 'no-preference',
    employeeId: null,
    name: null,
    imageUrl: null,
    imageAlternativeUrl: null,
    firstAvailableDate: today,
    isNoPreferenceEmployee: true,
    availability: [],
    lastFitting: false,
    order: 0,
    slug: null,
    companyId: null,
  }
}

export const EmployeesList = ({ onClose }: EmployeesListProps) => {
  const { t } = useI18N()
  const { navigate, locationCode, wwLocationId, env, storeId } =
    useBookingStore()

  const advisorScreen = useStyleAdvisorScreen()

  // Hold everything — the advisor fetch included — while the flag could still
  // skip this screen. Every route into the list arrives through this mount (the
  // store locator, a `location-code` deep-link), so gating here covers them
  // all; acting before the answer is final would fetch a list nobody sees and
  // flash it on the way past.
  //
  // Which stores the flag targets is GrowthBook's rule, so this screen cannot
  // tell in advance whether it is one of them — every visitor waits for the
  // payload, then fetches. That is one small CDN read (localStorage-cached
  // after the first, and skipped entirely when the visitor asked for this
  // screen), and it buys a single advisor fetch instead of one here plus the
  // identical one Timeslots makes. A payload that never arrives resolves to
  // "off" — see `loadFeatures`.
  //
  // Both `'skip'` and `'deciding'` hold here; only `'skip'` acts, below. That
  // split is the whole reason the hook has three answers and not a boolean.
  const isResolvingSkip = advisorScreen !== 'show'

  const {
    data: employees,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['employees', locationCode, wwLocationId],
    queryFn: getEmployees,
    enabled: Boolean(locationCode || wwLocationId) && !isResolvingSkip,
    // Always hit the network when this screen mounts, and drop the cache
    // when it unmounts so returning via back never shows stale availability.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  // Show the loader for every fetch — including remounts after navigating back.
  const isLoading = isResolvingSkip || isPending || isFetching

  /**
   * Flag on: make the "No preference" choice on the visitor's behalf and hand
   * over to the timeslots — the same state a tap on the no-preference row
   * writes, minus the tap.
   *
   * `setState` rather than `navigate`, so the screen is replaced instead of
   * pushed: the back arrow keeps pointing at the store locator (or stays hidden
   * on a deep-link entry) rather than at a screen the visitor never saw.
   * `advisorStepSkipped` makes the skip stick: the timeslots drop their "See
   * all style advisors" link, so the list is reachable again only from the
   * no-availability state, and showing it clears the flag (RP-6088).
   *
   * No `selectedEmployee` is carried: Timeslots fetches the no-preference
   * advisor with its availability through `getNoPreferenceEmployee`, which hits
   * the same `v4/employees` endpoint this screen would have called — so
   * skipping the fetch here costs nothing and saves a screen's worth of wait.
   */
  useEffect(() => {
    if (advisorScreen === 'deciding') return

    const store = useBookingStore.getState()

    if (advisorScreen === 'show') {
      if (store.advisorStepSkipped)
        store.setState({ advisorStepSkipped: false })
      return
    }

    // The booking really is a no-preference booking, and without these two the
    // funnel loses step 2 — every visitor on the flag would read as a drop-off
    // between the store and the employees list. Fired before the `setState`
    // below: `flowProperties` reads `state.screen` at call time, so once the
    // screen flips to `timeslots` both events would report step 3 instead.
    Avo.bookingToolNoPreferenceSelected(flowProperties())
    Avo.bookingToolStepCompleted(flowProperties())

    store.setState({
      employeeId: undefined,
      employeeName: undefined,
      wwResourceId: undefined,
      selectedEmployee: undefined,
      advisorStepSkipped: true,
      screen: 'timeslots',
    })
  }, [advisorScreen])

  // Same load analytics as the old Stencil employees-list (after data is ready).
  // Guarded so a manual retry or the `refetchOnMount: 'always'` refetch this
  // screen always does cannot count as a second view of the same mount.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (isLoading || isError || !employees || viewedRef.current) return
    viewedRef.current = true
    Avo.bookingToolStepViewed(flowProperties())
  }, [isLoading, isError, employees])

  const erroredRef = useRef(false)
  useEffect(() => {
    if (!isError) {
      erroredRef.current = false
      return
    }
    if (erroredRef.current) return
    erroredRef.current = true
    Avo.bookingToolError({
      ...flowProperties(),
      errorType: ErrorType.EMPLOYEES_FAILED,
    })
  }, [isError])

  const handleEmployeeSelect = (employee: Employee) => {
    // Only selectable when the advisor has availability (matches old bt-row gate).
    if (!employee.firstAvailableDate) return

    const employeeId =
      employee.isNoPreferenceEmployee || !employee.employeeId
        ? undefined
        : employee.employeeId

    useBookingStore.getState().setState({
      employeeId,
      employeeName: employee.isNoPreferenceEmployee
        ? undefined
        : (employee.name ?? undefined),
      wwResourceId: employee.isNoPreferenceEmployee
        ? undefined
        : (employee.id ?? undefined),
      // Carry the full employee object so Timeslots can use it without a
      // redundant API call.  Cleared when navigating back from Timeslots.
      selectedEmployee: employee,
    })

    // Old tool: name present → employee selected; otherwise no-preference.
    if (employee.name) Avo.bookingToolEmployeeSelected(flowProperties())
    else Avo.bookingToolNoPreferenceSelected(flowProperties())
    Avo.bookingToolStepCompleted(flowProperties())

    navigate('timeslots')
  }

  const { noPreference: apiNoPreference, rest: unsortedEmployees } = employees
    ? splitEmployees(employees)
    : { noPreference: null, rest: [] }

  const regularEmployees = sortByEarliestAvailability(unsortedEmployees)

  const noPreferenceEmployee = resolveNoPreferenceEmployee(apiNoPreference)

  return (
    <ScreenLayout
      // No title while the skip is unresolved: this screen's own heading
      // appearing for a frame is the one part of it the handover to the
      // timeslots cannot take back.
      title={isResolvingSkip ? undefined : t('employeesList.title')}
      titleTestId="employees-list-heading"
      onClose={onClose}
    >
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader ariaLabel={t('common.loading')} className="h-auto w-auto" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-24 px-32 text-center">
          <p
            className="text-base leading-[24px] text-text"
            data-testid="employees-list-error"
          >
            {t('common.error')}
          </p>
          <Button
            variant="outlined"
            size="m"
            onClick={() => refetch()}
            data-testid="employees-list-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && employees && (
        <div
          className="flex-1 overflow-y-auto px-16"
          data-testid="employees-list-container"
        >
          {/* No Preference is always the first row — no EPP link */}
          <EmployeeRow
            employee={noPreferenceEmployee}
            onSelect={handleEmployeeSelect}
            eppUrl={null}
          />

          {regularEmployees.map((employee) => (
            <EmployeeRow
              key={employee.id}
              employee={employee}
              onSelect={handleEmployeeSelect}
              eppUrl={getEppUrl({ employee, env, storeId, locationCode })}
            />
          ))}
        </div>
      )}
    </ScreenLayout>
  )
}
